import { useRef } from 'react';

import { Box, Center, Flex, Heading, Show, Text } from '@chakra-ui/react';
import { useInView } from 'framer-motion';
import Image from 'next/image';

import { StickyAdsContainer } from '@/components/atoms';
import { ParsedHTMLText } from '@/components/functional';
import { LetsBuildStandaloneSection } from '@/components/sections/common';
import { InventoryDefinitionReadMore } from '@/components/sections/inventory/InventoryDefinitionReadMore/InventoryDefinitionReadMore';
import { LazyTitlesByLetter } from '@/components/sections/inventory/TitlesList/LazyTitlesByLetter';
import { InventoryDefinitionArticle } from '@/components/sections/inventory/typings';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { ImageFragment } from '@/graphql/generated';
import { getImageAlt, getImageSrcFromAttributes } from '@/utils/images';
import { Alphabet } from '@/utils/models/inventory-items';

import { InventoryDefinitionBackground } from '../InventoryDefinitionBackground';
import { inventoryDefinitionStyles } from './inventory-definition-content-styles';

interface InventoryDefinitionContentProps {
  title: string;
  slug: string;
  content: string;
  articles?: InventoryDefinitionArticle[];
  doodle?: ImageFragment | null;
  letters?: Alphabet[];
  stickyAds?: Array<{
    __typename?: 'StickyAdEntity';
    id?: string | null;
    attributes?: {
      __typename?: 'StickyAd';
      headingAd: string;
      textAd: string;
      buttonCaptionAd: string;
      buttonUrlAd: string;
      hasDarkBackground: boolean;
      imageAd?: {
        __typename?: 'UploadFileEntityResponse';
        data?: {
          __typename?: 'UploadFileEntity';
          attributes?: {
            __typename?: 'UploadFile';
            url: string;
            alternativeText?: string | null;
          } | null;
        } | null;
      } | null;
    } | null;
  }>;
}

export const InventoryDefinitionContent = ({
  title,
  doodle,
  content,
  articles,
  slug,
  letters,
  stickyAds,
}: InventoryDefinitionContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef);
  const shortTitle = slug.replaceAll('-', ' ');

  return (
    <SectionWrapper>
      <SectionContentWrapper ref={containerRef} maxW={letters ? '100%' : '846px'}>
        <InventoryDefinitionBackground isInView={isInView} />
        {letters ? (
          <Flex flexDirection={{ base: 'column', md: 'row' }} alignItems="flex-start" gap={{ base: 10, md: 14 }}>
            <Show above="md">
              <Box
                as="aside"
                w={{ base: '100%', md: 'auto' }}
                h="200vh"
                maxW="244px"
                overflowY={{ md: 'auto' }}
                position={{ md: 'sticky' }}
                top={{ md: 24 }}
                pr={{ md: 2 }}
              >
                {letters.map(({ letter }) => (
                  <Box key={`inventory-sidebar-letter-${letter}`} mb={8}>
                    <Heading as="h3" color="text-dark.500" fontFamily="heading" fontSize="md" mb={3}>
                      {letter}
                    </Heading>
                    <LazyTitlesByLetter
                      letter={letter}
                      isSingleColumn
                      basePath="/glossary"
                      withLeftMargin={false}
                      visibleCount={3}
                      showToggle
                      moreLabel="Show more"
                      lessLabel="Show less"
                    />
                  </Box>
                ))}
              </Box>
            </Show>

            <Flex flex="1" flexDirection={{ base: 'column', xl: 'row' }} alignItems="flex-start" gap={{ xl: 8 }}>
              <Box flex="1" minW={0}>
                {doodle?.data ? (
                  <Center mb="12">
                    <Image
                      src={getImageSrcFromAttributes(doodle.data?.attributes, 'medium')}
                      alt={getImageAlt(doodle.data?.attributes, title)}
                      loading="eager"
                      width={250}
                      height={250}
                      style={{
                        objectFit: 'contain',
                        height: '250px',
                      }}
                    />
                  </Center>
                ) : null}
                <Text fontWeight="normal" textTransform="capitalize" color="purple.500" textAlign="center">
                  {shortTitle}
                </Text>
                <Heading
                  as="h1"
                  variant={{
                    base: 'component-heading-large/xl',
                    sm: 'component-heading-large/2xl',
                    lg: 'component-heading-large/3xl',
                  }}
                  color="purple.500"
                  textAlign="center"
                  my="12"
                >
                  {title}
                </Heading>
                <ParsedHTMLText content={content} innerStyles={inventoryDefinitionStyles} />
                {Array.isArray(articles) && articles.length > 0 ? (
                  <InventoryDefinitionReadMore articles={articles} />
                ) : null}
              </Box>

              {stickyAds && stickyAds.length > 0 && (
                <Flex
                  w={{ base: '100%', lg: 'auto' }}
                  justifyContent={{ base: 'center', lg: 'flex-start' }}
                  my={{ base: '60px', lg: '0' }}
                  flexShrink={0}
                >
                  <StickyAdsContainer stickyAds={stickyAds} />
                </Flex>
              )}
            </Flex>
          </Flex>
        ) : (
          <>
            {doodle?.data ? (
              <Center mb="12">
                <Image
                  src={getImageSrcFromAttributes(doodle.data?.attributes, 'medium')}
                  alt={getImageAlt(doodle.data?.attributes, title)}
                  loading="eager"
                  width={250}
                  height={250}
                  style={{
                    objectFit: 'contain',
                    height: '250px',
                  }}
                />
              </Center>
            ) : null}
            <Text fontWeight="normal" textTransform="capitalize" color="purple.500" textAlign="center">
              {shortTitle}
            </Text>
            <Heading
              as="h1"
              variant={{
                base: 'component-heading-large/xl',
                sm: 'component-heading-large/2xl',
                lg: 'component-heading-large/3xl',
              }}
              color="purple.500"
              textAlign="center"
              my="12"
            >
              {title}
            </Heading>
            <ParsedHTMLText content={content} innerStyles={inventoryDefinitionStyles} />
            {Array.isArray(articles) && articles.length > 0 ? (
              <InventoryDefinitionReadMore articles={articles} />
            ) : null}
          </>
        )}
      </SectionContentWrapper>
      <Box w="100%" overflow="hidden">
        <LetsBuildStandaloneSection />
      </Box>
    </SectionWrapper>
  );
};
